import React, { HTMLAttributes } from 'react'
import { StyleObject } from 'styletron-standard'
import { type ExtendedTheme, styled } from '../../theme'
import primitives from '../../theme/primitives'
import getStylesFromFnOrObject from '../../utils/getStylesFromFnOrObject'

export type CardProps<T extends {} = {}> = T & {
    tag?: keyof JSX.IntrinsicElements
    styleOverrides?: ((props: T & { $theme: ExtendedTheme }) => StyleObject) | StyleObject
} & HTMLAttributes<HTMLElement>

const StyledCard = styled<
    'section',
    { styleOverrides?: (props: { $theme: ExtendedTheme }) => StyleObject | undefined }
>('section', ({ styleOverrides, $theme }) => ({
    backgroundColor: $theme.colors.white,
    borderRadius: $theme.borders.surfaceBorderRadius,
    outline: `1px solid ${$theme.colors.mono200}`,
    borderStyle: 'none',
    padding: $theme.sizing.scale900,
    fontFamily: primitives.primaryFontFamily,
    ...styleOverrides?.({ $theme })
}))

/**
 *
 * @prop `tag`: Any JSX.IntrinsicElements like `div, a, h1, section, ...`, default to `section`
 * @prop `styleOverrides`: {@link StyleObject} or style fucntion ((props: { $theme: ExtendedTheme }) => StyleObject)
 * @returns Styled JSX.IntrinsicElements
 */
export default function Card({
    tag = 'section',
    styleOverrides,
    children,
    ...attributes
}: React.PropsWithChildren<CardProps>) {
    const styleObject = getStylesFromFnOrObject(styleOverrides)

    return (
        <StyledCard {...attributes} styleOverrides={styleObject} $as={tag}>
            {children}
        </StyledCard>
    )
}
